import axios from "axios";
import moment from "moment";
import { dbService } from "../firebaseInstance";
import { arrayUnion } from "firebase/firestore";
import i18n from '../i18n/config';
import { uploadAndMakePublicAvatar } from './publicAvatarUtils';
import { 
  calculateDocumentSize,
  generateDocumentList,
  initializeShardingMetadata 
} from './firestoreShardingUtils';

// === Collection Management Functions ===

/**
 * Pure 함수: 사용자의 컬렉션 목록 가져오기
 * @param {string} userID - 사용자 ID
 * @returns {Promise<Array>} 컬렉션 목록
 */
export const getMyCollections = async (userID) => {
	console.log("[getMyCollections] START - userID:", userID);
	try {
		// Firebase 초기화 체크
		if (!dbService) {
			throw new Error("Firebase dbService is not initialized");
		}
		
		const userRef = dbService.collection("user").doc(userID);
		console.log("[getMyCollections] Getting user document...");
		
		const userDoc = await userRef.get();
		console.log("[getMyCollections] User doc exists:", userDoc.exists);

		if (userDoc.exists) {
			const userData = userDoc.data();
			console.log("[getMyCollections] User data:", {
				hasCollectionList: !!userData.collectionList,
				collectionListLength: userData.collectionList?.length || 0,
				collectionList: userData.collectionList
			});
			
			const collectionList = userData.collectionList || [];
			
			if (collectionList.length === 0) {
				console.log("[getMyCollections] No collections found for user");
				return [];
			}
			
			const collections = await Promise.all(
				collectionList.map(async (collectionId, index) => {
					try {
						console.log(`[getMyCollections] Fetching collection ${index + 1}/${collectionList.length}: ${collectionId}`);
						const collectionRef = dbService
							.collection("collection")
							.doc(collectionId);
						const collectionDoc = await collectionRef.get();
						
						if (collectionDoc.exists) {
							const collectionData = collectionDoc.data();
							const threadList = collectionData.threadList || [];
							console.log(`[getMyCollections] Collection ${collectionId} has ${threadList.length} threads`);

							const metric = {
								replies: 0,
								reposts: 0,
								likes: 0,
								views: 0,
							};

							threadList.forEach((thread) => {
								metric.replies += thread.replies || 0;
								metric.reposts += thread.reposts || 0;
								metric.likes += thread.likes || 0;
								metric.views += thread.views || 0;
							});

							collectionData.metric = metric;

							return { id: collectionId, ...collectionData };
						} else {
							console.error(
								`[getMyCollections] No such collection document with ID: ${collectionId}`
							);
							return null;
						}
					} catch (collectionError) {
						console.error(`[getMyCollections] Error fetching collection ${collectionId}:`, collectionError);
						return null;
					}
				})
			);

			const filteredCollections = collections.filter((collection) => collection !== null);
			console.log(`[getMyCollections] Successfully loaded ${filteredCollections.length} collections`);
			return filteredCollections;
		} else {
			console.log("[getMyCollections] User document does not exist, creating...");
			// User 문서가 없으면 생성
			await userRef.set({
				collectionList: [],
				createdAt: new Date().toISOString()
			}, { merge: true });
			
			return [];
		}
	} catch (error) {
		console.error("Error fetching collections: ", error);
		throw error;
	}
};

/**
 * Pure 함수: 새 컬렉션 생성
 * @param {Object} data - 컬렉션 데이터
 * @returns {Promise<Object>} 생성된 컬렉션 정보
 */
export const createCollection = async (data) => {
	try {
		// lastUpdated 필드 추가
		const collectionData = {
			...data,
			lastUpdated: new Date().toISOString()
		};

		const collectionRef = dbService.collection("collection");
		const docRef = await collectionRef.add(collectionData);

		const userRef = dbService.collection("user").doc(data.userID);
		await userRef.update({
			collectionList: arrayUnion(docRef.id),
		});

		return { id: docRef.id, ...collectionData };
	} catch (error) {
		console.error("Error adding collection: ", error);
		throw error;
	}
};

/**
 * Pure 함수: 단일 컬렉션 정보 가져오기
 * @param {string} collectionId - 컬렉션 ID
 * @returns {Promise<Object>} 컬렉션 데이터
 */
export const getCollection = async (collectionId) => {
	try {
		const collectionRef = dbService.collection("collection").doc(collectionId);
		const collectionDoc = await collectionRef.get();
		
		if (!collectionDoc.exists) {
			throw new Error(`Collection with ID ${collectionId} not found`);
		}
		
		return { id: collectionId, ...collectionDoc.data() };
	} catch (error) {
		console.error("Error fetching collection: ", error);
		throw error;
	}
};

/**
 * Pure 함수: 컬렉션 업데이트
 * @param {string} collectionID - 컬렉션 ID
 * @param {Object} data - 업데이트할 데이터
 * @returns {Promise<Object>} 업데이트된 컬렉션 정보
 */
export const updateCollection = async (collectionID, data) => {
	try {
		// lastUpdated 필드 자동 업데이트
		const updateData = {
			...data,
			lastUpdated: new Date().toISOString()
		};

		const collectionRef = dbService.collection("collection").doc(collectionID);
		await collectionRef.update(updateData);

		return { id: collectionID, ...updateData };
	} catch (error) {
		console.error("Error updating collection: ", error);
		throw error;
	}
};

/**
 * Pure 함수: 컬렉션 삭제
 * @param {string} collectionID - 컬렉션 ID
 * @param {string} userID - 사용자 ID (선택적)
 * @returns {Promise<void>}
 */
export const deleteCollection = async (collectionID, userID = null) => {
	try {
		// 컬렉션 문서 삭제
		const collectionRef = dbService.collection("collection").doc(collectionID);
		await collectionRef.delete();
		
		// 사용자의 collectionList에서 제거
		if (userID) {
			const userRef = dbService.collection("user").doc(userID);
			const userDoc = await userRef.get();
			
			if (userDoc.exists) {
				const userData = userDoc.data();
				const updatedCollectionList = (userData.collectionList || []).filter(
					id => id !== collectionID
				);
				
				await userRef.update({
					collectionList: updatedCollectionList
				});
			}
		}
	} catch (error) {
		console.error("Error deleting collection: ", error);
		throw error;
	}
};

/**
 * Pure 함수: 모든 컬렉션 가져오기
 * @returns {Promise<Array>} 모든 컬렉션 목록
 */
export const getAllCollection = async () => {
	try {
		const collectionRef = dbService.collection("collection");
		const snapshot = await collectionRef.get();
		const collections = snapshot.docs.map((doc) => ({
			id: doc.id,
			...doc.data(),
		}));
		return collections;
	} catch (error) {
		console.error("Error fetching all collections: ", error);
		throw error;
	}
};

/**
 * Pure 함수: 사용자 인사이트 데이터 가져오기
 * @param {string} userID - 사용자 ID
 * @returns {Promise<Object>} 사용자 데이터
 */
export const getUserInsight = async (userID) => {
	try {
		const userRef = dbService.collection("user").doc(userID);
		const userDoc = await userRef.get();
		
		if (!userDoc.exists) {
			throw new Error(`User with ID ${userID} not found`);
		}
		
		return userDoc.data();
	} catch (error) {
		console.error("Error fetching user insight: ", error);
		throw error;
	}
};

/**
 * Pure 함수: 모든 사용자 정보 가져오기 (ThreaderList용)
 * @returns {Promise<Array>} 모든 사용자 목록
 */
export const getAllUsers = async () => {
	try {
		const userDocRef = dbService.collection("user");
		const userDoc = await userDocRef.get();
		const usersData = [];

		for (const doc of userDoc.docs) {
			const userData = doc.data();
			const userInsightDoc = await doc.ref
				.collection("insight")
				.doc("insight")
				.get();
			const userInsight = userInsightDoc.exists ? userInsightDoc.data() : {};
			
			// 프로필이 있는 유저만 포함
			if (userData.profile) {
				usersData.push({ 
					id: doc.id,
					...userData, 
					insight: userInsight 
				});
			}
		}

		return usersData;
	} catch (error) {
		console.error("Error fetching all users: ", error);
		throw error;
	}
};

const urlBase = "https://graph.threads.net/v1.0/";

// === 사용자 프로필 관련 함수 ===

/**
 * Pure 함수: 사용자 프로필 정보 가져오기
 * @param {string} token - 액세스 토큰
 * @returns {Promise<Object>} 프로필 데이터
 */
export const getProfilePure = async (token) => {
	if (!token) {
		throw new Error("No access token provided");
	}
	
	const urlProfile = `${urlBase}me`;

	try {
		const response = await axios.get(urlProfile, {
			params: {
				fields: "id,username,name,threads_profile_picture_url,threads_biography",
				access_token: token,
			},
		});
		return response.data;
	} catch (error) {
		// 토큰 관련 에러인 경우 localStorage 정리
		if (error.response?.status === 400 || error.response?.status === 401) {
			localStorage.removeItem("accessToken");
			localStorage.removeItem("userID");
		}
		throw error;
	}
};

/**
 * Pure 함수: 사용자 로그인 시 프로필 및 아바타 처리
 * @param {string} token - 액세스 토큰
 * @returns {Promise<Object>} 처리된 프로필 데이터
 */
export const processUserLoginPure = async (token) => {
	try {
		// 1. 프로필 정보 가져오기
		const profileData = await getProfilePure(token);
		
		// 2. 아바타 업로드 및 Public 설정 (백그라운드 실행, 선택적)
		if (profileData.id && profileData.threads_profile_picture_url) {
			console.log('🔄 Starting avatar upload for user:', profileData.id);
			
			// 백그라운드에서 아바타 업로드 실행 (실패해도 로그인 진행)
			// 권한 오류 시 자동으로 건너뛰기
			uploadAndMakePublicAvatar(profileData.id, profileData.threads_profile_picture_url)
				.then(publicUrl => {
					console.log('✅ Avatar uploaded successfully:', publicUrl);
				})
				.catch(error => {
					console.warn('⚠️ Avatar upload failed (non-critical):', error.message);
					// 권한 오류인 경우 추가 정보 제공
					if (error.code === 'storage/unauthorized') {
						console.info('💡 Firebase Storage 권한이 필요합니다. 아바타 동기화 버튼을 사용해주세요.');
					}
				});
		}
		
		return profileData;
		
	} catch (error) {
		console.error("Error processing user login:", error);
		throw error;
	}
};

// === 에러 처리 및 재시도 로직 ===

/**
 * Exponential Backoff를 사용한 재시도 함수
 */
const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
	for (let attempt = 0; attempt < maxRetries; attempt++) {
		try {
			return await fn();
		} catch (error) {
			const isLastAttempt = attempt === maxRetries - 1;
			
			// 마지막 시도거나 재시도 불가능한 에러면 바로 throw
			if (isLastAttempt || error.response?.status === 401 || error.response?.status === 403) {
				throw error;
			}
			
			// 네트워크 에러나 5xx 에러만 재시도
			const shouldRetry = !error.response || error.response.status >= 500 || error.code === 'NETWORK_ERROR';
			if (!shouldRetry) {
				throw error;
			}
			
			const delay = baseDelay * Math.pow(2, attempt);
			await new Promise(resolve => setTimeout(resolve, delay));
		}
	}
};

/**
 * Pure 함수: 개별 스레드 인사이트 가져오기 (재시도 로직 포함)
 * @param {string} threadId - 스레드 ID
 */
export const getThreadInsightsPure = async (threadId) => {
	const token = localStorage.getItem("accessToken");
	
	try {
		return await retryWithBackoff(async () => {
			const response = await axios.get(
				`${urlBase}${threadId}/insights`,
				{
					params: {
						metric: "views, likes,replies,reposts",
						access_token: token,
					},
				}
			);
			return response.data.data.reduce((acc, item) => {
				acc[item.name] = item.values[0].value;
				return acc;
			}, {});
		});
	} catch (error) {
		console.error(`Error fetching insights for thread ${threadId}:`, error);
		return {};
	}
};

/**
 * 네트워크 상태 기반 최적 배치 설정 계산
 */
const getOptimalBatchSettings = () => {
	try {
		const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
		
		if (connection) {
			// 네트워크 타입에 따른 최적화
			switch (connection.effectiveType) {
				case '4g':
					return { chunkSize: 100, delay: 50 };
				case '3g':
					return { chunkSize: 50, delay: 100 };
				case '2g':
					return { chunkSize: 25, delay: 200 };
				default:
					return { chunkSize: 50, delay: 100 };
			}
		}
		
		// 기본값 (네트워크 정보 없을 때)
		return { chunkSize: 50, delay: 100 };
	} catch (error) {
		console.warn('Failed to detect network condition:', error);
		return { chunkSize: 50, delay: 100 };
	}
};

/**
 * 배치 스레드 인사이트 로딩 (최적화된 청크 단위)
 * @param {Array} threads - 스레드 배열
 * @param {number} customChunkSize - 커스텀 청크 크기 (선택적)
 * @param {Function} onProgress - 진행률 콜백 (선택적)
 */
export const getBatchThreadInsights = async (threads, customChunkSize = null, onProgress = null) => {
	if (!threads || threads.length === 0) return [];
	
	const batchSettings = getOptimalBatchSettings();
	const chunkSize = customChunkSize || batchSettings.chunkSize;
	const delay = batchSettings.delay;
	
	if (onProgress) {
		onProgress({
			stage: 'batch-insights',
			message: i18n.t('loading.analyzing_threads'),
			total: Math.ceil(threads.length / chunkSize),
			completed: 0
		});
	}
	
	const results = [];
	
	for (let i = 0; i < threads.length; i += chunkSize) {
		const chunk = threads.slice(i, i + chunkSize);
		const batchNumber = Math.floor(i / chunkSize) + 1;
		const totalBatches = Math.ceil(threads.length / chunkSize);
		
		// 진행률 업데이트
		if (onProgress) {
			onProgress({
				stage: 'batch-insights',
				message: i18n.t('loading.analyzing_threads'),
				subMessage: i18n.t('loading.batch_processing', { current: batchNumber, total: totalBatches }),
				total: totalBatches,
				completed: batchNumber - 1
			});
		}
		
		try {
			// 청크 내에서 병렬 처리
			const chunkResults = await Promise.all(
				chunk.map(async (thread) => {
					if (thread.media_type !== "REPOST_FACADE") {
						const insights = await getThreadInsightsPure(thread.id);
						return { ...thread, ...insights };
					} else {
						return { ...thread, views: 0, likes: 0, replies: 0, reposts: 0 };
					}
				})
			);
			
			results.push(...chunkResults);
			
			// 최적화된 지연 시간 적용
			if (i + chunkSize < threads.length) {
				await new Promise(resolve => setTimeout(resolve, delay));
			}
		} catch (error) {
			console.error(`❌ Error processing batch ${batchNumber}:`, error);
			// 에러 시 인사이트 없이 원본 스레드 추가
			results.push(...chunk.map(thread => ({ 
				...thread, 
				views: 0, likes: 0, replies: 0, reposts: 0 
			})));
		}
	}
	
	return results;
};

/**
 * Pure 함수: 사용자 인사이트 가져오기 (재시도 로직 포함)
 * @param {number} since - Unix timestamp
 * @param {number} until - Unix timestamp
 */
export const getInsightPure = async (since, until) => {
	const token = localStorage.getItem("accessToken");
	const userID = localStorage.getItem("userID");
	const urlInsight = `${urlBase}${userID}/threads_insights`;

	try {
		return await retryWithBackoff(async () => {
			return await axios.get(urlInsight, {
				params: {
					since: since,
					until: until,
					metric: "views, followers_count, likes, replies, reposts",
					access_token: token,
				},
			});
		});
	} catch (error) {
		console.error("Error fetching insights:", error);
		throw error;
	}
};

/**
 * Pure 함수: 스레드 댓글 가져오기 (재시도 로직 포함)
 * @param {string} threadId - 스레드 ID
 */
export const getRepliesPure = async (threadId) => {
	const token = localStorage.getItem("accessToken");
	const url = `${urlBase}${threadId}/replies`;
	
	try {
		return await retryWithBackoff(async () => {
			const response = await axios.get(url, {
				params: {
					fields: "id,text,username,user_id",
					reverse: false,
					access_token: token,
				},
			});
			return response.data.data;
		});
	} catch (error) {
		console.error(`Error fetching replies for thread ${threadId}:`, error);
		return []; // 댓글 로딩 실패 시 빈 배열 반환
	}
};

/**
 * Pure 함수: 스레드의 전체 대화(conversation) 가져오기 (중첩 댓글 포함)
 * @param {string} threadId - 스레드 ID
 */
export const getConversationPure = async (threadId) => {
	const token = localStorage.getItem("accessToken");
	const url = `${urlBase}${threadId}/conversation`;
	
	try {
		return await retryWithBackoff(async () => {
			const response = await axios.get(url, {
				params: {
					fields: "id,text,username,user_id,timestamp,replied_to,is_reply,is_reply_owned_by_me,root_post,permalink",
					reverse: false, // 시간순 정렬
					access_token: token,
				},
			});
		
			return response.data.data;
		});
	} catch (error) {
		console.error(`Error fetching conversation for thread ${threadId}:`, error);
		return []; // 대화 로딩 실패 시 빈 배열 반환
	}
};

/**
 * Pure 함수: 최근 3개월 스레드의 대화 데이터 가져오기 (테스트용)
 */
export const fetchRecentConversationsForTest = async (threads) => {
	if (!threads || !Array.isArray(threads) || threads.length === 0) {
		return [];
	}

	// 최근 3개월 내 스레드만 필터링 (이미 필터링된 상태로 받음)
	const recentThreads = threads;

	
	if (recentThreads.length === 0) {
		return [];
	}

	try {
		const allConversations = await Promise.all(
			recentThreads.map(async (thread) => {
				try {
					const conversation = await getConversationPure(thread.id);
					
					return { 
						threadId: thread.id, 
						threadText: thread.text,
						threadTimestamp: thread.timestamp,
						conversation 
					};
				} catch (error) {
					console.error(`❌ Failed to fetch conversation for thread ${thread.id}:`, error);
					return { 
						threadId: thread.id, 
						threadText: thread.text,
						threadTimestamp: thread.timestamp,
						conversation: [] 
					};
				}
			})
		);



		return allConversations;
	} catch (error) {
		console.error("❌ Error fetching recent conversations:", error);
		throw error;
	}
};

/**
 * Pure 함수: 특정 기간의 스레드 데이터만 가져옴
 * @param {number} since - Unix timestamp
 * @param {number} until - Unix timestamp
 * @param {string|null} after - Pagination token
 */
export const fetchThreadsForPeriod = async (since, until, after = null) => {
	const token = localStorage.getItem("accessToken");
	const baseUrl = `${urlBase}me/threads`;

	const params = {
		fields: "id,text,timestamp,media_product_type,username,media_type,media_url,permalink",
		since: since,
		until: until,
		access_token: token,
		limit: 300,
		...(after && { after }),
	};

	const response = after
		? await axios.get(after)
		: await axios.get(baseUrl, { params });

	const threads = response.data.data || [];
	
	// 각 스레드에 인사이트 데이터 추가
	const threadsWithInsights = await Promise.all(
		threads.map(async (thread) => {
			if (thread.media_type !== "REPOST_FACADE") {
				const insights = await getThreadInsightsPure(thread.id);
				return { ...thread, ...insights };
			} else {
				return { ...thread, views: 0, likes: 0, replies: 0, reposts: 0 };
			}
		})
	);

	return {
		threads: threadsWithInsights.filter((thread) => thread.text),
		paging: response.data.paging
	};
};

/**
 * Pure 함수: 특정 기간의 인사이트 데이터만 가져옴
 * @param {number} since - Unix timestamp
 * @param {number} until - Unix timestamp
 */
export const fetchInsightsForPeriod = async (since, until) => {
	const insightsResponse = await getInsightPure(since, until);
	const data = insightsResponse.data.data || [];

	if (data.length === 0) {
		return {
			views: 0,
			likes: 0,
			replies: 0,
			reposts: 0,
			profileViews: []
		};
	}

	// 메트릭 계산
	const profileViews = data[0]?.values?.map((item) => ({
		x: item.end_time,
		y: item.value,
	})) || [];

	return {
		views: data[0]?.total_value || 0,
		likes: data[2]?.total_value || 0,
		replies: data[3]?.total_value || 0,
		reposts: data[4]?.total_value || 0,
		profileViews
	};
};

/**
 * Pure 함수: 스레드 배열을 주별로 그룹화
 */
const createWeeklyList = (threads) => {
	const weeklyList = threads.reduce((acc, thread) => {
		const threadDate = moment(thread.timestamp);
		const weekTitle = threadDate.week() - threadDate.startOf("month").week() + 1;

		if (!acc[weekTitle]) {
			acc[weekTitle] = [];
		}
		acc[weekTitle].push(thread);
		return acc;
	}, {});

	return Object.entries(weeklyList)
		.map(([title, posts]) => ({ title, posts }))
		.sort((a, b) => moment(b.posts[0].timestamp) - moment(a.posts[0].timestamp));
};

/**
 * Pure 함수: 스레드에 랭킹 정보 추가
 */
const addRankingsToThreads = (threads) => {
	// Sort posts by views and add viewRank
	const viewSorted = [...threads].sort((a, b) => b.views - a.views);
	const viewRanked = viewSorted.map((post, index) => ({
		...post,
		viewRank: index + 1,
	}));

	// Sort posts by engagement score and add engagementRank
	const engagementSorted = [...viewRanked].sort((a, b) => {
		const scoreA = (a.likes * 1 + a.replies * 2) / a.views;
		const scoreB = (b.likes * 1 + b.replies * 2) / b.views;
		return scoreB - scoreA;
	});
	const engagementRanked = engagementSorted.map((post, index) => ({
		...post,
		engagementRank: index + 1,
	}));

	// Sort by timestamp in descending order (newest first)
	return engagementRanked.sort(
		(a, b) => new Date(b.timestamp) - new Date(a.timestamp)
	);
};

/**
 * Pure 함수: 월별 데이터 생성 (Dashboard용)
 */
export const processMonthlyData = (threads, insights, month, year) => {
	if (!threads || threads.length === 0) {
		return {
			threads: {
				month,
				year,
				week: []
			},
			insight: {
				month,
				year,
				views: 0,
				likes: 0,
				replies: 0,
				reposts: 0,
				profileViews: []
			}
		};
	}

	// 스레드에 랭킹 추가
	const rankedThreads = addRankingsToThreads(threads);
	
	// 주별로 그룹화
	const weeklyList = createWeeklyList(rankedThreads);
	
	// 주별 데이터에도 랭킹 적용
	const rankedWeeklyList = weeklyList.map((week) => {
		const rankedPosts = addRankingsToThreads(week.posts);
		return {
			...week,
			posts: rankedPosts,
		};
	});

	// 스레드 메트릭 합계 계산
	const threadMetricSums = threads.reduce(
		(sums, thread) => {
			sums.likes += thread.likes || 0;
			sums.replies += thread.replies || 0;
			sums.reposts += thread.reposts || 0;
			sums.views += thread.views || 0;
			return sums;
		},
		{ likes: 0, replies: 0, reposts: 0, views: 0 }
	);

	return {
		threads: {
			month,
			year,
			week: rankedWeeklyList
		},
		insight: {
			month,
			year,
			...threadMetricSums,
			profileViews: insights.profileViews || []
		}
	};
};

/**
 * 통합 함수: 월별 데이터 완전 가져오기 (API 호출 + 데이터 처리)
 */
export const fetchMonthlyData = async (month, year) => {
	const startDate = moment(`${year}-${month}-01`).startOf('month');
	const endDate = moment(startDate).endOf('month');
	
	// Threads API는 Unix timestamp를 요구함
	const since = startDate.unix();
	// until이 현재 시간보다 미래면 현재 시간으로 제한
	const now = Math.floor(Date.now() / 1000);
	const until = Math.min(endDate.unix(), now);
	
	

	try {
		// 병렬로 스레드와 인사이트 데이터 가져오기
		const [threadsResult, insights] = await Promise.all([
			fetchThreadsForPeriod(since, until),
			fetchInsightsForPeriod(since, until)
		]);

		// 데이터 처리
		return processMonthlyData(threadsResult.threads, insights, month, year);
	} catch (error) {
		console.error(`Error fetching monthly data for ${year}-${month}:`, error);
		throw error;
	}
};

/**
 * Lifetime 데이터에서 월별 데이터 추출
 */
export const extractMonthlyDataFromLifetime = (lifetimeThreads, month, year) => {
	if (!lifetimeThreads || !Array.isArray(lifetimeThreads) || lifetimeThreads.length === 0) {
		return null;
	}

	const startDate = moment(`${year}-${month}-01`).startOf('month');
	const endDate = moment(startDate).endOf('month');

	// 해당 월의 스레드 필터링
	const monthThreads = lifetimeThreads.filter(thread => {
		const threadDate = moment(thread.timestamp);
		return threadDate.isBetween(startDate, endDate, null, '[]');
	});

	if (monthThreads.length === 0) {
		return null;
	}

	// 기본 인사이트 데이터 생성 (실제 profileViews 데이터는 별도 처리 필요)
	const insights = {
		views: monthThreads.reduce((sum, t) => sum + (t.views || 0), 0),
		likes: monthThreads.reduce((sum, t) => sum + (t.likes || 0), 0),
		replies: monthThreads.reduce((sum, t) => sum + (t.replies || 0), 0),
		reposts: monthThreads.reduce((sum, t) => sum + (t.reposts || 0), 0),
		profileViews: [] // 이 부분은 lifetime insight에서 별도 추출 필요
	};

	return processMonthlyData(monthThreads, insights, month, year);
};

// === Firestore + API Hybrid System ===

const DEFAULT_SINCE = 1688540400; // 2023-07-01

// === 로딩 진행률 추적 시스템 ===

/**
 * 로딩 진행률을 localStorage에 저장
 */
const saveLoadingProgress = (userID, progress) => {
  try {
    const progressData = {
      timestamp: Date.now(),
      stage: progress.stage,
      completed: progress.completed || 0,
      total: progress.total || 0,
      lastProcessedId: progress.lastProcessedId || null,
      since: progress.since || null,
      until: progress.until || null
    };
    localStorage.setItem(`loading_progress_${userID}`, JSON.stringify(progressData));
  } catch (error) {
    console.warn('Failed to save loading progress:', error);
  }
};

/**
 * 저장된 로딩 진행률 복구
 */
const getLoadingProgress = (userID) => {
  try {
    const saved = localStorage.getItem(`loading_progress_${userID}`);
    if (!saved) return null;
    
    const progress = JSON.parse(saved);
    // 30분 이내의 진행률만 유효
    if (Date.now() - progress.timestamp > 30 * 60 * 1000) {
      localStorage.removeItem(`loading_progress_${userID}`);
      return null;
    }
    return progress;
  } catch (error) {
    console.warn('Failed to get loading progress:', error);
    return null;
  }
};

/**
 * 로딩 완료 시 진행률 데이터 삭제
 */
const clearLoadingProgress = (userID) => {
  try {
    localStorage.removeItem(`loading_progress_${userID}`);
  } catch (error) {
    console.warn('Failed to clear loading progress:', error);
  }
};

/**
 * Pure 함수: Firestore + API 하이브리드 라이프타임 스레드 데이터 가져오기
 * dataFetch.js의 핵심 로직을 완전히 적용
 */
export const fetchLifetimeThreadsPure = async (userID) => {
	console.log('🚀 fetchLifetimeThreadsPure with Firestore + API hybrid approach');
	
	const token = localStorage.getItem("accessToken");
	let allThreads = [];
	let nextAfter = null;

	try {
		// 0단계: 중단된 로딩 진행률 확인
		const savedProgress = getLoadingProgress(userID);

		// 1단계: Firestore에서 사용자 문서 확인 (dataFetch.js:390-407)
		const userRef = dbService.collection("user").doc(userID);
		const userDoc = await userRef.get();

		const today = new Date();
		let since = DEFAULT_SINCE; // 1688540400 = 2023-07-01
		let until = Math.floor(today.getTime() / 1000);

		// 진행률 저장 시작
		saveLoadingProgress(userID, {
			stage: 'firestore-check',
			since,
			until
		});

		// 2단계: lastUpdated 확인 및 기존 Firestore 데이터 로드 (다중 문서 지원)
		if (userDoc.exists && userDoc.data().lastUpdated) {
			since = userDoc.data().lastUpdated; // 🎯 핵심: 마지막 업데이트 이후만 가져오기
			
			// 메타데이터 확인
			const userData = userDoc.data();
			const metadata = userData.threadsMetadata;
			
			// 단일 문서 최적화 (95% 사용자)
			if (!metadata || metadata.totalDocuments === 1) {
				const threadsDoc = await userRef
					.collection("threads")
					.doc("threads")
					.get();

				if (threadsDoc.exists) {
					allThreads = threadsDoc.data().list || []; // 🎯 기존 데이터 즉시 로드
				}
			} else {
				// 다중 문서 로딩
				console.log(`📚 Loading ${metadata.totalDocuments} documents for user ${userID}`);
				
				const documentIds = generateDocumentList(metadata.totalDocuments);
				const threadsSnapshots = await Promise.all(
					documentIds.map(docId => 
						userRef.collection("threads").doc(docId).get()
					)
				);
				
				// 모든 문서에서 스레드 병합
				allThreads = [];
				threadsSnapshots.forEach((doc, index) => {
					if (doc.exists) {
						const data = doc.data();
						if (data.list && Array.isArray(data.list)) {
							allThreads.push(...data.list);
							console.log(`📄 Document ${documentIds[index]}: ${data.list.length} threads`);
						}
					}
				});
				
				console.log(`✅ Total threads loaded from ${metadata.totalDocuments} documents: ${allThreads.length}`);
			}
		}

		// 3단계: API에서 since 이후의 새 데이터만 가져오기
		const baseUrl = `${urlBase}me/threads`;
		let newThreads = [];
		
		console.log('🔍 자동 업데이트 API 호출 파라미터:', {
			since: since,
			until: until,
			sinceDate: new Date(since * 1000).toISOString(),
			untilDate: new Date(until * 1000).toISOString(),
			timeRange: `${Math.floor((until - since) / 60 / 60 / 24)}일간`
		});

		do {
			const params = {
				fields: "id,text,timestamp,media_product_type,username,media_type,media_url,permalink",
				since: since, // 🎯 핵심: lastUpdated 이후만
				until: until,
				access_token: token,
				limit: 100,
				...(nextAfter && { after: nextAfter }),
			};
			
			const response = nextAfter
				? await axios.get(nextAfter)
				: await axios.get(baseUrl, { params });

			const responseThreads = response.data.data || [];
			
			console.log('📡 API 응답:', {
				responseCount: responseThreads.length,
				hasNext: !!response.data.paging?.next,
				firstThreadTimestamp: responseThreads[0]?.timestamp ? new Date(responseThreads[0].timestamp).toISOString() : null,
				lastThreadTimestamp: responseThreads[responseThreads.length - 1]?.timestamp ? new Date(responseThreads[responseThreads.length - 1].timestamp).toISOString() : null
			});

			if (responseThreads.length > 0) {
				// 배치 처리로 인사이트 가져오기
				const threadsWithInsights = await getBatchThreadInsights(responseThreads);

				const filteredThreads = threadsWithInsights.filter(
					(thread) => thread.text
				);
				
				console.log('✅ 필터링 후 스레드:', {
					original: responseThreads.length,
					afterInsights: threadsWithInsights.length,
					afterFilter: filteredThreads.length
				});

				newThreads = [...newThreads, ...filteredThreads];
			}
			
			nextAfter = response.data.paging?.next || null;
		} while (nextAfter);
		
		console.log('🎯 최종 새 스레드 결과:', {
			newThreadsCount: newThreads.length,
			existingThreadsCount: allThreads.length,
			willSaveToFirestore: newThreads.length > 0
		});



		// 4단계: 새 데이터를 Firestore에 저장 (950KB 임계점 관리 포함)
		if (newThreads.length > 0) {
			try {
				// 메타데이터에서 타겟 문서 확인
				const userData = userDoc.data();
				const metadata = userData?.threadsMetadata || {};
				const targetDoc = metadata.nextTargetDocument || "threads";
				
				const threadsRef = userRef.collection("threads").doc(targetDoc);
				
				// 업데이트 시도
				await threadsRef.update({
					list: arrayUnion(...newThreads)
				}).catch(async (error) => {
					// 문서가 없으면 생성
					if (error.code === 'not-found') {
						await threadsRef.set({
							list: newThreads,
							createdAt: new Date().toISOString()
						});
					} else {
						throw error;
					}
				});
				
				// 업데이트 후 크기 체크
				const updatedDoc = await threadsRef.get();
				const size = calculateDocumentSize(updatedDoc.data());
				
				// 950KB 도달 시 메타데이터 갱신
				if (size > 950 * 1024) {
					const nextNumber = targetDoc === "threads" ? 1 : 
						parseInt(targetDoc.split("-")[1]) + 1;
					
					await userRef.update({
						threadsMetadata: {
							...metadata,
							nextTargetDocument: `threads-${nextNumber}`,
							isApproachingLimit: true,
							currentDocumentSize: size,
							totalDocuments: (metadata.totalDocuments || 1),
							[`documentSizes.${targetDoc}`]: {
								size: size,
								count: updatedDoc.data().list.length
							},
							lastUpdatedAt: new Date().toISOString()
						}
					});
					
					console.log(`⚠️ Document ${targetDoc} approaching 1MB limit (${(size/1024/1024).toFixed(2)}MB). Next target: threads-${nextNumber}`);
				}

				await userRef.update({
					lastUpdated: Math.floor(Date.now() / 1000), // 🎯 현재 시간으로 업데이트
				});

			} catch (firestoreError) {
				// 1MB 초과 에러 자동 복구
				if (firestoreError.code === 'resource-exhausted' || 
					firestoreError.message.includes('exceeds the maximum allowed size')) {
					
					console.log("🔄 1MB limit exceeded, creating new document for recovery...");
					
					// 새 문서로 재시도
					const userData = userDoc.data();
					const metadata = userData?.threadsMetadata || {};
					const currentDoc = metadata.nextTargetDocument || "threads";
					const nextDoc = currentDoc === "threads" ? "threads-1" : 
						`threads-${parseInt(currentDoc.split("-")[1]) + 1}`;
					
					await userRef.collection("threads").doc(nextDoc).set({
						list: newThreads,
						createdAt: new Date().toISOString()
					});
					
					// 메타데이터 업데이트
					await userRef.update({
						threadsMetadata: {
							...metadata,
							nextTargetDocument: nextDoc,
							totalDocuments: (metadata.totalDocuments || 1) + 1,
							isApproachingLimit: false, // 새 문서이므로 리셋
							lastUpdatedAt: new Date().toISOString()
						}
					});
					
					console.log(`✅ Recovery successful. Created new document: ${nextDoc}`);
				} else {
					console.error("⚠️ Failed to save to Firestore (non-critical):", firestoreError);
				}
			}

			// 5단계: Firestore에서 전체 데이터 다시 로드 (sharding 적용)
			// 새 데이터가 저장되었으므로 실제 Firestore 상태를 반영하여 전체 스레드를 다시 불러옴
			const userData = userDoc.data();
			const metadata = userData?.threadsMetadata;
			
			// 단일 문서 최적화 (95% 사용자)
			if (!metadata || metadata.totalDocuments === 1) {
				const threadsDoc = await userRef
					.collection("threads")
					.doc("threads")
					.get();

				if (threadsDoc.exists) {
					allThreads = threadsDoc.data().list || [];
				}
			} else {
				// 다중 문서 로딩 (sharding 적용된 사용자)
				console.log(`📚 Reloading ${metadata.totalDocuments} documents after update`);
				
				const documentIds = generateDocumentList(metadata.totalDocuments);
				const threadsSnapshots = await Promise.all(
					documentIds.map(docId => 
						userRef.collection("threads").doc(docId).get()
					)
				);
				
				// 모든 문서에서 스레드 병합
				allThreads = [];
				threadsSnapshots.forEach((doc, index) => {
					if (doc.exists) {
						const data = doc.data();
						if (data.list && Array.isArray(data.list)) {
							allThreads.push(...data.list);
							console.log(`📄 Document ${documentIds[index]}: ${data.list.length} threads`);
						}
					}
				});
				
				console.log(`✅ Total threads reloaded: ${allThreads.length}`);
			}
		}

		// 6단계: 날짜순 정렬 및 반환
		const sortedThreads = allThreads.sort((a, b) =>
			moment(b.timestamp).diff(moment(a.timestamp))
		);
		
		// 로딩 완료 시 진행률 데이터 삭제
		clearLoadingProgress(userID);
		
		return sortedThreads;
		
	} catch (error) {
		console.error("Error fetching lifetime threads:", error);
		// 에러 시 기존 Firestore 데이터라도 반환
		if (allThreads.length > 0) {
			return allThreads;
		}
		throw error;
	}
};

/**
 * Pure 함수: 라이프타임 인사이트 데이터 가져오기 (순수 API 호출)
 */
export const fetchLifetimeInsightsPure = async () => {
	const insightSince = moment("2024-06-02").startOf("day").unix();
	const until = Math.floor(new Date().getTime() / 1000);

	try {
		const insightsResponse = await getInsightPure(insightSince, until);
		const data = insightsResponse.data.data || [];
		
		if (data.length === 0) {
			return {
				profileViewsTotal: 0,
				profileViews: [],
				followers: 0,
				likes: 0,
				replies: 0,
				reposts: 0,
			};
		}

		const totalViews = data[0].values.reduce(
			(sum, item) => sum + item.value,
			0
		);
		
		const profileViews = data[0].values.map((item) => ({
			x: item.end_time,
			y: item.value,
		}));

		const filteredProfileViews = profileViews.reduce((acc, curr) => {
			if (acc.length === 0 && curr.y !== 0) {
				acc.push(curr);
			} else if (acc.length > 0) {
				acc.push(curr);
			}
			return acc;
		}, []);

		return {
			profileViewsTotal: totalViews,
			profileViews: filteredProfileViews,
			followers: data[1]?.total_value || 0,
			likes: data[2]?.total_value || 0,
			replies: data[3]?.total_value || 0,
			reposts: data[4]?.total_value || 0,
		};
	} catch (error) {
		console.error("Error fetching lifetime insights:", error);
		throw error;
	}
};

/**
 * Pure 함수: 주간 참여도 데이터 계산
 */
export const computeWeeklyEngagement = (threads) => {
	if (!threads || !Array.isArray(threads) || threads.length === 0) {
		return [];
	}

	const weeklyEngagement = threads.reduce((acc, thread) => {
		const threadDate = moment(thread.timestamp);
		const weekStart = threadDate.startOf("week");
		const weekKey = weekStart.format("YYYY-MM-DD");

		let weekData = acc.find((item) => item.date === weekKey);
		if (!weekData) {
			weekData = {
				date: weekKey,
				year: weekStart.year(),
				month: weekStart.month() + 1,
				week: weekStart.week(),
				likes: 0,
				replies: 0,
				reposts: 0,
				views: 0,
				engagementRate: 0,
			};
			acc.push(weekData);
		}

		weekData.likes += thread.likes || 0;
		weekData.replies += thread.replies || 0;
		weekData.reposts += thread.reposts || 0;
		weekData.views += thread.views || 0;

		return acc;
	}, []);

	weeklyEngagement.forEach((week) => {
		week.engagementRate =
			week.views > 0
				? ((week.likes * 1 + week.replies * 2 + week.reposts * 3) /
						week.views) *
				  100
				: 0;
	});

	const sortedWeeklyEngagement = weeklyEngagement.sort((a, b) =>
		moment(a.date).diff(moment(b.date))
	);

	// 차트 형식으로 변환
	return [
		sortedWeeklyEngagement.map((week) => ({
			date: new Date(week.date),
			value: week.likes,
		})),
		sortedWeeklyEngagement.map((week) => ({
			date: new Date(week.date),
			value: week.replies,
		})),
		sortedWeeklyEngagement.map((week) => ({
			date: new Date(week.date),
			value: week.reposts,
		})),
		sortedWeeklyEngagement.map((week) => ({
			date: new Date(week.date),
			value: week.engagementRate,
		})),
	];
};

/**
 * Pure 함수: 모든 스레드의 댓글 데이터 가져오기 (기존 방식 - 호환성 유지)
 */
export const fetchAllRepliesPure = async (threads) => {
	if (!threads || !Array.isArray(threads) || threads.length === 0) {
		return [];
	}

	try {
		const allReplies = await Promise.all(
			threads.map(async (thread) => {
				try {
					const replies = await getRepliesPure(thread.id);
					return { threadId: thread.id, replies };
				} catch (error) {
					console.error(
						`Failed to fetch replies for thread ${thread.id}:`,
						error
					);
					return { threadId: thread.id, replies: [] };
				}
			})
		);

		return allReplies;
	} catch (error) {
		console.error("Error fetching all replies:", error);
		throw error;
	}
};

/**
 * Pure 함수: 모든 스레드의 대화(conversation) 데이터 가져오기 (새로운 방식)
 */
export const fetchAllConversationsPure = async (threads) => {
	if (!threads || !Array.isArray(threads) || threads.length === 0) {
		return [];
	}

	try {
		const allConversations = await Promise.all(
			threads.map(async (thread) => {
				try {
					const conversation = await getConversationPure(thread.id);
					return { 
						threadId: thread.id, 
						threadText: thread.text,
						threadTimestamp: thread.timestamp,
						conversation 
					};
				} catch (error) {
					console.error(
						`Failed to fetch conversation for thread ${thread.id}:`,
						error
					);
					return { 
						threadId: thread.id, 
						threadText: thread.text,
						threadTimestamp: thread.timestamp,
						conversation: [] 
					};
				}
			})
		);

		return allConversations;
	} catch (error) {
		console.error("Error fetching all conversations:", error);
		throw error;
	}
};

/**
 * Pure 함수: 댓글별 사용자 데이터 계산 (기존 방식 - 호환성 유지)
 */
export const computeRepliesByUser = (repliesData) => {
	if (!repliesData || !Array.isArray(repliesData)) {
		return [];
	}

	const userRepliesMap = {};

	repliesData.forEach(({ replies }) => {
		if (replies && Array.isArray(replies)) {
			replies.forEach((reply) => {
				const username = reply.username || 'anonymous';
				if (!userRepliesMap[username]) {
					userRepliesMap[username] = 0;
				}
				userRepliesMap[username]++;
			});
		}
	});

	return Object.entries(userRepliesMap)
		.map(([username, count]) => ({ username, count }))
		.sort((a, b) => b.count - a.count);
};

/**
 * Pure 함수: Conversation 데이터에서 사용자별 상호작용 분석 (새로운 방식)
 * 같은 스레드 내에서 서로 나눈 대화를 시간순으로 분석
 */
export const analyzeUserInteractions = (conversationsData) => {
	if (!conversationsData || !Array.isArray(conversationsData)) {
		return [];
	}

	const myUserId = localStorage.getItem("userID");
	const userInteractionsMap = {};

	console.log('🔍 Analyzing user interactions with myUserId:', myUserId);
	console.log('📅 Processing conversations from recent 3 months threads only');

	conversationsData.forEach(({ threadId, conversation, threadText, threadTimestamp }) => {
		if (!conversation || !Array.isArray(conversation)) return;
		// Step 1: 다른 사용자들의 댓글 찾기
		const othersComments = conversation.filter(comment => 
			comment.user_id !== myUserId && comment.username
		);

		// Step 2: 내가 단 댓글/대댓글 찾기  
		const myComments = conversation.filter(comment => 
			comment.user_id === myUserId || comment.is_reply_owned_by_me
		);



		// Step 3: 각 사용자별로 상호작용 매핑
		othersComments.forEach(otherComment => {
			const username = otherComment.username;
			
			if (!userInteractionsMap[username]) {
				userInteractionsMap[username] = {
					username,
					conversations: [], // 시간순 대화 배열
					totalInteractions: 0,
					theirCommentsCount: 0,
					myRepliesCount: 0
				};
			}

			// 그 사용자의 댓글 추가
			userInteractionsMap[username].conversations.push({
				type: 'their_comment',
				threadId,
				threadText: threadText?.substring(0, 100) + '...',
				threadTimestamp,
				comment: {
					...otherComment,
					permalink: otherComment.permalink || `https://www.threads.net/@${otherComment.username}`,
				},
				timestamp: otherComment.timestamp
			});
			userInteractionsMap[username].theirCommentsCount++;

			// 내가 이 댓글에 대댓글을 달았는지 확인
			const myRepliesToThis = myComments.filter(myComment => {
				const repliedToId = typeof myComment.replied_to === 'object' 
					? myComment.replied_to?.id 
					: myComment.replied_to;
				return repliedToId === otherComment.id;
			});



			// 내 대댓글들도 추가
			myRepliesToThis.forEach(myReply => {
				userInteractionsMap[username].conversations.push({
					type: 'my_reply',
					threadId,
					threadText: threadText?.substring(0, 100) + '...',
					threadTimestamp,
					comment: {
						...myReply,
						permalink: myReply.permalink || `https://www.threads.net/`
					},
					timestamp: myReply.timestamp
				});
				userInteractionsMap[username].myRepliesCount++;
			});
		});
	});

	// Step 4: 각 사용자별로 대화를 시간순 정렬하고 통계 계산
	const result = Object.values(userInteractionsMap).map(user => ({
		...user,
		conversations: user.conversations.sort((a, b) => 
			new Date(a.timestamp) - new Date(b.timestamp)
		),
		totalInteractions: user.theirCommentsCount + user.myRepliesCount,
		count: user.theirCommentsCount + user.myRepliesCount // 기존 UI 호환성
	})).sort((a, b) => b.totalInteractions - a.totalInteractions);


	return result;
};

/**
 * Pure 함수: 박스플롯 데이터 계산
 */
export const computeBoxPlotData = (threads) => {
	if (!threads || !Array.isArray(threads) || threads.length === 0) {
		return {};
	}

	const metrics = ['views', 'likes', 'replies', 'reposts'];
	const boxPlotData = {};

	metrics.forEach(metric => {
		const values = threads
			.map(thread => thread[metric] || 0)
			.filter(value => value >= 0)
			.sort((a, b) => a - b);

		if (values.length === 0) {
			boxPlotData[metric] = { min: 0, max: 0, avg: 0 };
			return;
		}

		const min = values[0];
		const max = values[values.length - 1];
		const avg = values.reduce((sum, val) => sum + val, 0) / values.length;

		boxPlotData[metric] = { min, max, avg: Math.round(avg) };
	});

	return boxPlotData;
};

/**
 * 날짜 범위 포맷팅 유틸리티
 */
const formatDateRange = (since, isFromCache) => {
	if (isFromCache) {
		const sinceDate = new Date(since * 1000);
		return i18n.t('loading.since_update', { 
			year: sinceDate.getFullYear(), 
			month: sinceDate.getMonth() + 1 
		});
	} else {
		return i18n.t('loading.full_period');
	}
};

/**
 * 최적화된 라이프타임 데이터 로딩 (Firestore 캐시 + API 업데이트)
 * @param {string} userID - 사용자 ID
 * @param {Function} onProgress - 진행 상황 콜백
 * @param {boolean} forceUpdate - 캐시를 무시하고 강제 업데이트 (기본값: false)
 */
export const fetchLifetimeThreadsOptimized = async (userID, onProgress = null, forceUpdate = false) => {
	console.log("🔍 DEBUG: fetchLifetimeThreadsOptimized 진입", { userID, forceUpdate });
	try {
		// 1단계: Firestore에서 캐시된 데이터 즉시 확인
		if (onProgress) onProgress({
			stage: 'cache-check',
			message: i18n.t('loading.checking_cache'),
			subMessage: i18n.t('loading.firestore_cache_check')
		});

		const userRef = dbService.collection("user").doc(userID);
		const userDoc = await userRef.get();
		
		let cachedThreads = [];
		let dateRangeText = '';
		
		if (userDoc.exists) {
			// 메타데이터 확인 및 다중 문서 로딩
			const userData = userDoc.data();
			const metadata = userData?.threadsMetadata;
			
			// 단일 문서 최적화 (95% 사용자)
			if (!metadata || metadata.totalDocuments === 1) {
				const threadsDoc = await userRef.collection("threads").doc("threads").get();
				if (threadsDoc.exists) {
					cachedThreads = threadsDoc.data().list || [];
				}
			} else {
				// 다중 문서 로딩
				console.log(`📚 Loading ${metadata.totalDocuments} documents for cache check`);
				
				const documentIds = generateDocumentList(metadata.totalDocuments);
				const threadsSnapshots = await Promise.all(
					documentIds.map(docId => 
						userRef.collection("threads").doc(docId).get()
					)
				);
				
				// 모든 문서에서 스레드 병합
				cachedThreads = [];
				threadsSnapshots.forEach((doc, index) => {
					if (doc.exists) {
						const data = doc.data();
						if (data.list && Array.isArray(data.list)) {
							cachedThreads.push(...data.list);
							console.log(`📄 Cache document ${documentIds[index]}: ${data.list.length} threads`);
						}
					}
				});
				
				console.log(`✅ Total cached threads from ${metadata.totalDocuments} documents: ${cachedThreads.length}`);
			}
			
			// lastUpdated가 있으면 캐시된 데이터 우선 반환
			if (userData?.lastUpdated && cachedThreads.length > 0) {
				dateRangeText = formatDateRange(userData.lastUpdated, true);
				
				if (onProgress) onProgress({ 
					stage: 'cached', 
					message: i18n.t('loading.cache_loaded'),
					subMessage: `${dateRangeText} - ${i18n.t('loading.threads_found', { count: cachedThreads.length })}`,
					data: cachedThreads 
				});
				
				// 캐시된 데이터가 있고 lastUpdated가 최근(24시간 이내)이면 즉시 반환
				const lastUpdated = userData.lastUpdated;
				const now = Math.floor(Date.now() / 1000);
				const dayInSeconds = 2 * 60 * 60; // 2시간으로 단축 (기존 24시간)
				const timeDiff = now - lastUpdated;
				
				console.log("🔍 DEBUG: 캐시 신선도 체크", {
					lastUpdated,
					now,
					timeDiff,
					timeDiffHours: timeDiff / 3600,
					dayInSeconds,
					isFresh: timeDiff < dayInSeconds,
					hasThreads: cachedThreads.length > 0,
					forceUpdate
				});
				
				if (!forceUpdate && now - lastUpdated < dayInSeconds && cachedThreads.length > 0) {
					console.log("🔍 DEBUG: 캐시가 신선하여 조기 반환");
					if (onProgress) onProgress({ 
						stage: 'complete', 
						message: i18n.t('loading.cache_fresh'),
						subMessage: `${dateRangeText} - ${i18n.t('loading.total_threads', { count: cachedThreads.length })}`,
						data: cachedThreads 
					});
					return cachedThreads;
				} else {
					console.log("🔍 DEBUG: 캐시 무시하고 API 호출", { 
						reason: forceUpdate ? "강제 업데이트" : "캐시 만료 또는 스레드 없음" 
					});
				}
			}
		}
		
		// lastUpdated가 없거나 오래된 경우에만 전체 업데이트
		if (!userDoc.exists || !userDoc.data().lastUpdated) {
			dateRangeText = formatDateRange(DEFAULT_SINCE, false);
		}
		
		// 2단계: 최신 데이터 업데이트 (필요한 경우에만)
		console.log("🔍 DEBUG: fetchLifetimeThreadsPure 호출 직전");
		if (onProgress) onProgress({ 
			stage: 'updating', 
			message: i18n.t('loading.loading_threads'),
			subMessage: dateRangeText
		});
		
		const updatedData = await fetchLifetimeThreadsPure(userID);
		console.log("🔍 DEBUG: fetchLifetimeThreadsPure 완료", { dataLength: updatedData?.length });
		
		if (onProgress) onProgress({ 
			stage: 'complete', 
			message: i18n.t('loading.data_updated'),
			subMessage: i18n.t('loading.total_threads', { count: updatedData.length }),
			data: updatedData 
		});
		
		return updatedData;
		
	} catch (error) {
		console.error('Optimized data loading failed:', error);
		
		// 에러 시 Firestore 캐시된 데이터라도 반환 시도 (다중 문서 지원)
		try {
			const userRef = dbService.collection("user").doc(userID);
			const userDoc = await userRef.get();
			let fallbackData = [];
			
			if (userDoc.exists) {
				const metadata = userDoc.data()?.threadsMetadata;
				
				// 단일 문서 폴백
				if (!metadata || metadata.totalDocuments === 1) {
					const threadsDoc = await userRef.collection("threads").doc("threads").get();
					if (threadsDoc.exists) {
						fallbackData = threadsDoc.data().list || [];
					}
				} else {
					// 다중 문서 폴백
					const documentIds = generateDocumentList(metadata.totalDocuments);
					const threadsSnapshots = await Promise.all(
						documentIds.map(docId => 
							userRef.collection("threads").doc(docId).get()
						)
					);
					
					threadsSnapshots.forEach((doc) => {
						if (doc.exists) {
							const data = doc.data();
							if (data.list && Array.isArray(data.list)) {
								fallbackData.push(...data.list);
							}
						}
					});
				}
				
				if (fallbackData.length > 0) {
					if (onProgress) onProgress({ 
						stage: 'error', 
						message: `Update failed, using ${fallbackData.length} cached threads`, 
						data: fallbackData 
					});
					return fallbackData;
				}
			}
		} catch (fallbackError) {
			console.error('Failed to load fallback data:', fallbackError);
		}
		
		throw error;
	}
};

// ==================================================================================
// === LEGACY FUNCTIONS (from dataFetch.js) - 이전 버전 호환성을 위한 레거시 함수들 ===
// ==================================================================================

/**
 * 비동기 Firestore 저장 함수 (백그라운드 실행)
 * @deprecated - 레거시 함수, 현재 사용되지 않음
 */
const saveInsightsToFirestoreAsync = async (userRef, lifetimeInsight, userID) => {
	try {
		await userRef.collection("insight").doc("insight").set(
			{ list: lifetimeInsight },
			{ merge: true }
		);
	} catch (error) {
		console.error("⚠️ Failed to save insights to Firestore (non-critical):", error);
		try {
			const errorLogRef = dbService.collection("errorLogs").doc(userID);
			await errorLogRef.update({
				errors: arrayUnion({
					type: "firestore_insights_save",
					error: error.message,
					timestamp: new Date(),
				}),
			});
		} catch (logError) {
			console.error("Failed to log error:", logError);
		}
	}
};

/**
 * Legacy: 사용자 프로필 조회 (특정 ID)
 * @deprecated - 현재 사용되지 않음
 */
export const getUserProfileLegacy = async (id) => {
	const token = localStorage.getItem("accessToken");
	const urlProfile = `${urlBase}${id}`;
	return await axios.get(urlProfile, {
		params: {
			fields: "id,username,name,threads_profile_picture_url,threads_biography",
			access_token: token,
		},
	});
};

/**
 * Legacy: 특정 월의 profileViews만 가져오는 함수
 * @deprecated - 현재 사용되지 않음, fetchMonthlyData 함수 사용 권장
 */
export const fetchMonthlyProfileViewsLegacy = async (year, month) => {
	try {
		const startDate = moment(`${year}-${month}-01`).startOf('month');
		const endDate = moment(startDate).endOf('month');
		
		const since = startDate.format('YYYY-MM-DD');
		const until = endDate.format('YYYY-MM-DD');
		
		const insightsResponse = await getInsightPure(since, until);
		
		if (!insightsResponse || !insightsResponse.data || !insightsResponse.data.data || insightsResponse.data.data.length === 0) {
			return null;
		}
		
		const monthlyView = insightsResponse.data.data[0].values.map((item) => ({
			x: item.end_time,
			y: item.value,
		}));
		
		return {
			month,
			year,
			profileViews: monthlyView
		};
	} catch (error) {
		console.error(`Error fetching profileViews for ${year}-${month}:`, error);
		return null;
	}
};

/**
 * Legacy: 개별 스레드 인사이트 가져오기 (재시도 로직 없음)
 * @deprecated - getThreadInsightsPure 사용 권장
 */
export const getThreadInsightsLegacy = async (threadId) => {
	const token = localStorage.getItem("accessToken");
	try {
		const response = await axios.get(
			`https://graph.threads.net/v1.0/${threadId}/insights`,
			{
				params: {
					metric: "views, likes,replies,reposts",
					access_token: token,
				},
			}
		);
		return response.data.data.reduce((acc, item) => {
			acc[item.name] = item.values[0].value;
			return acc;
		}, {});
	} catch (error) {
		console.error(`Error fetching insights for thread ${threadId}:`, error);
		return {};
	}
};

/**
 * Legacy: 스레드 배열을 주별로 그룹화 (구버전)
 * @deprecated - 내부 createWeeklyList 함수 사용 권장
 */
export const createWeeklyListLegacy = (threads) => {
	const weeklyList = threads.reduce((acc, thread) => {
		const threadDate = moment(thread.timestamp);
		const weekTitle =
			threadDate.week() - threadDate.startOf("month").week() + 1;

		if (!acc[weekTitle]) {
			acc[weekTitle] = [];
		}
		acc[weekTitle].push(thread);
		return acc;
	}, {});

	return Object.entries(weeklyList)
		.map(([title, posts]) => ({ title, posts }))
		.sort(
			(a, b) => moment(b.posts[0].timestamp) - moment(a.posts[0].timestamp)
		);
};

/**
 * Legacy: 월별 스레드 데이터 가져오기 (구버전 - 복잡한 상태 관리 포함)
 * @deprecated - fetchMonthlyData 함수 사용 권장
 */
export const fetchMonthlyThreadsLegacy = async (
	after = null,
	since,
	until,
	setPostLoading,
	setThreadsList,
	threadsList,
	insightList,
	setInsightList,
	month,
	year
) => {
	setPostLoading(true);
	const token = localStorage.getItem("accessToken");
	try {
		const baseUrl = "https://graph.threads.net/v1.0/me/threads";

		const params = {
			fields:
				"id,text,timestamp,media_product_type,username,media_type,media_url,permalink",
			since: since,
			until: until,
			access_token: token,
			limit: 300,
			...(after && { after }),
		};

		const response = after
			? await axios.get(after)
			: await axios.get(baseUrl, { params });
		const newThreads = response.data.data || [];
		const threadsWithInsights = await Promise.all(
			newThreads.map(async (thread) => {
				if (thread.media_type !== "REPOST_FACADE") {
					const insights = await getThreadInsightsLegacy(thread.id);
					return { ...thread, ...insights };
				} else {
					return { ...thread, views: 0, likes: 0, replies: 0, reposts: 0 };
				}
			})
		);

		const filteredThreads = threadsWithInsights.filter((thread) => thread.text);

		const weeklyList = createWeeklyListLegacy(filteredThreads);
		// Create a new list with ranks added to each post
		const rankedWeeklyList = weeklyList.map((week) => {
			// Sort posts by views and add viewRank
			const viewSorted = [...week.posts].sort((a, b) => b.views - a.views);
			const viewRanked = viewSorted.map((post, index) => ({
				...post,
				viewRank: index + 1,
			}));

			// Sort posts by engagement score and add engagementRank
			const engagementSorted = [...viewRanked].sort((a, b) => {
				const scoreA = (a.likes * 1 + a.replies * 2) / a.views;
				const scoreB = (b.likes * 1 + b.replies * 2) / b.views;
				return scoreB - scoreA;
			});
			const engagementRanked = engagementSorted.map((post, index) => ({
				...post,
				engagementRank: index + 1,
			}));

			// Sort by timestamp in descending order (newest first)
			const timestampSorted = engagementRanked.sort(
				(a, b) => new Date(b.timestamp) - new Date(a.timestamp)
			);

			return {
				...week,
				posts: timestampSorted,
			};
		});

		const mothlyData = {
			month: month,
			year: year,
			week: rankedWeeklyList,
		};
		const previousThreads = threadsList;
		const newThreadsList = [...previousThreads, mothlyData];
		setThreadsList(newThreadsList);

		// Fetch insights data
		// use insight api call for merging profileViews to monthly threads object
		const insightsResponse = await getInsightPure(since, until);
		const newInsights = insightsResponse.data || [];

		// Combine previous insights with new insights
		// Sum up the values for each metric across all filtered threads
		const metricSums = filteredThreads.reduce(
			(sums, thread) => {
				sums.likes += thread.likes || 0;
				sums.replies += thread.replies || 0;
				sums.reposts += thread.reposts || 0;
				sums.views += thread.views || 0;
				return sums;
			},
			{ likes: 0, replies: 0, reposts: 0, views: 0 }
		);

		// Add the sums to the newInsights object
		newInsights.metricSums = metricSums;
		// Create monthlyView variable from newInsights.data[0].values
		const monthlyView = newInsights.data[0].values.map((item) => ({
			x: item.end_time,
			y: item.value,
		}));

		const updatedInsightList = [
			...(insightList || []),
			{
				month: month,
				year: year,
				profileViews: monthlyView,
				...metricSums,
			},
		];
		setInsightList(updatedInsightList);
	} catch (error) {
		console.error("Error fetching threads:", error);
		
		// Log error to Firestore (non-blocking)
		try {
			const userID = localStorage.getItem("userID");
			if (userID) {
				const errorLogRef = dbService.collection("errorLogs").doc(userID);
				await errorLogRef.update({
					errors: arrayUnion({
						type: "fetchMonthlyThreads",
						error: error.message,
						timestamp: new Date(),
					}),
				});
			}
		} catch (logError) {
			console.error("Failed to log error to Firestore:", logError);
		}
		
		throw error;
	} finally {
		setPostLoading(false);
	}
};

/**
 * Legacy: 라이프타임 인사이트 가져오기 (구버전 - 상태 설정 포함)
 * @deprecated - fetchLifetimeInsightsPure 사용 권장
 */
export const fetchLifetimeInsightsLegacy = async (
	since,
	until,
	setLifetimeInsightLoading
) => {
	setLifetimeInsightLoading(true);
	try {
		console.log("try fetchLifetimeInsights");
		const insightsResponse = await getInsightPure(since, until);
		const totalViews = insightsResponse.data.data[0].values.reduce(
			(sum, item) => sum + item.value,
			0
		);
		const profileViews = insightsResponse.data.data[0].values.map((item) => ({
			x: item.end_time,
			y: item.value,
		}));

		const filteredProfileViews = profileViews.reduce((acc, curr) => {
			if (acc.length === 0 && curr.y !== 0) {
				acc.push(curr);
			} else if (acc.length > 0) {
				acc.push(curr);
			}
			return acc;
		}, []);

		const lifetimeInsight = {
			profileViewsTotal: totalViews,
			profileViews: filteredProfileViews,
			followers: insightsResponse.data.data[1].total_value,
			likes: insightsResponse.data.data[2].total_value,
			replies: insightsResponse.data.data[3].total_value,
			reposts: insightsResponse.data.data[4].total_value,
		};
		return lifetimeInsight;
	} catch (error) {
		console.error("Error fetching lifetime insights:", error);

		try {
			const userID = localStorage.getItem("userID");
			const errorLogRef = dbService.collection("errorLogs").doc(userID);
			await errorLogRef.update({
				errors: arrayUnion({
					type: "fetchLifetimeInsights",
					error: error.message,
					timestamp: new Date(),
				}),
			});
		} catch (logError) {
			console.error("Failed to log error to Firestore:", logError);
		}

		throw error;
	} finally {
		setLifetimeInsightLoading(false);
	}
};

/**
 * Legacy: 라이프타임 스레드 데이터 가져오기 (구버전 - dispatch 기반)
 * @deprecated - fetchLifetimeThreadsOptimized 사용 권장
 */
export const fetchLifetimeThreadsLegacy = async (dispatch, userID) => {
	// ThreadsTypes import가 필요하지만 여기서는 레거시 보관용이므로 주석 처리
	// dispatch({ type: ThreadsTypes.SET_LOADING_STATE, payload: { key: 'threads', value: true } });
	
	const token = localStorage.getItem("accessToken");
	let allThreads = [];
	let nextAfter;

	try {
		const userRef = dbService.collection("user").doc(userID);
		const userDoc = await userRef.get();

		const today = new Date();
		let since = 1688540400;
		let until = Math.floor(today.getTime() / 1000);

		if (userDoc.exists && userDoc.data().lastUpdated) {
			since = userDoc.data().lastUpdated;
			const threadsDoc = await userRef
				.collection("threads")
				.doc("threads")
				.get();

			if (threadsDoc.exists) {
				allThreads = threadsDoc.data().list || [];
			}
		}

		const baseUrl = "https://graph.threads.net/v1.0/me/threads";
		do {
			const params = {
				fields:
					"id,text,timestamp,media_product_type,username,media_type,media_url,permalink",
				since: since,
				until: until,
				access_token: token,
				limit: 100,
				...(nextAfter && { after: nextAfter }),
			};
			
			const response = nextAfter
				? await axios.get(nextAfter)
				: await axios.get(baseUrl, { params });

			const newThreads = response.data.data || [];

			if (newThreads.length > 0) {
				const threadsWithInsights = await Promise.all(
					newThreads.map(async (thread) => {
						if (thread.media_type !== "REPOST_FACADE") {
							const insights = await getThreadInsightsLegacy(thread.id);
							return { ...thread, ...insights };
						} else {
							return { ...thread, views: 0, likes: 0, replies: 0, reposts: 0 };
						}
					})
				);

				const filteredThreads = threadsWithInsights.filter(
					(thread) => thread.text
				);

				if (filteredThreads.length > 0) {
					await userRef
						.collection("threads")
						.doc("threads")
						.set(
							{
								list: arrayUnion(...filteredThreads),
							},
							{ merge: true }
						);

					await userRef.update({
						lastUpdated: until,
					});

					allThreads = [...allThreads, ...filteredThreads];
				}
			}
			nextAfter = response.data.paging?.next || null;
		} while (nextAfter);

		// 아래 dispatch 호출들은 레거시 보관용이므로 주석 처리
		const allThreadsByDate = allThreads.sort((a, b) =>
			moment(b.timestamp).diff(moment(a.timestamp))
		);
		
		// dispatch({ type: ThreadsTypes.SET_THREADS, payload: allThreadsByDate });
		
		// ... 더 많은 레거시 로직들 (생략)
		
		return allThreadsByDate;
		
	} catch (error) {
		console.error("Error fetching lifetime threads:", error);
		if (allThreads.length > 0) {
			return allThreads;
		}
		throw error;
	}
};